import { useMemo, useRef, useState } from "react";

import {
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Plus,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  useOilTypeStockByPlant,
  useRawMaterialStockByPlant,
} from "~/hooks/purchasing";
import { useOilTypes } from "~/hooks/purchasing/useOilTypes";
import { useRawMaterials } from "~/hooks/purchasing/useRawMaterials";
import { useToast } from "~/hooks/use-toast";
import { useProducts } from "~/hooks/useProducts";
import {
  useAdjustmentReasonCodes,
  useCreateBulkAdjustments,
  useWarehouses,
} from "~/hooks/wms";
import {
  evaluateBulkRow,
  itemKeyOf,
  parseItemKey,
  resolveCode,
  type BulkRowEval,
} from "~/lib/wms/bulkAdjustmentRow";
import { parseBulkAdjustmentFile } from "~/lib/wms/parseBulkAdjustmentFile";
import { downloadBulkAdjustmentCatalog } from "~/services/wms/bulkAdjustmentTemplateService";
import { useProfileStore } from "~/store/profileStore";
import type {
  BulkAdjustmentItem,
  BulkAdjustmentResult,
  ItemType,
} from "~/types/stockAdjustment";
import { exportCsv } from "~/utils/csv";
import { formatCurrency } from "~/utils/helpers";

interface BulkStockAdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface EditRow {
  key: string;
  itemKey: string; // "<itemType>:<id>" from the picker; "" if unset
  location: string; // B1/B2/NC or P1/P2
  quantity: string; // signed (+/−), raw text
  unitCost: string; // optional, raw text
  lot: string; // optional (product decrement)
}

const PLANT_OPTIONS = ["P1", "P2"];

const tipoTag = (t: ItemType): string =>
  t === "product" ? "PT" : t === "oil" ? "Aceite" : "MP";

const parseNum = (s: string): number | null => {
  const t = s.trim().replace(/\s/g, "");
  if (!t) return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export function BulkStockAdjustmentDialog({
  open,
  onOpenChange,
  onSuccess,
}: BulkStockAdjustmentDialogProps) {
  const { toast } = useToast();
  const { id: userId } = useProfileStore();
  const createBulk = useCreateBulkAdjustments();

  const seq = useRef(0);
  const newRow = (): EditRow => ({
    key: String(seq.current++),
    itemKey: "",
    location: "",
    quantity: "",
    unitCost: "",
    lot: "",
  });

  const [reasonCodeId, setReasonCodeId] = useState("");
  const [rows, setRows] = useState<EditRow[]>(() => [newRow(), newRow(), newRow()]);
  const [results, setResults] = useState<BulkAdjustmentResult[] | null>(null);
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: reasonCodes = [] } = useAdjustmentReasonCodes();
  const { products = [] } = useProducts();
  const { data: rawMaterials = [] } = useRawMaterials();
  const { data: oilTypes = [] } = useOilTypes();
  const { data: warehouses = [] } = useWarehouses();
  const { data: oilStock } = useOilTypeStockByPlant();
  const { data: rmStock } = useRawMaterialStockByPlant();

  const reason = useMemo(
    () => reasonCodes.find((r) => r.id === reasonCodeId) ?? null,
    [reasonCodes, reasonCodeId]
  );

  const productsById = useMemo(
    () => new Map(products.map((p) => [p.id, p])),
    [products]
  );
  const oilsById = useMemo(
    () => new Map(oilTypes.map((o) => [o.id, o])),
    [oilTypes]
  );
  const rmsById = useMemo(
    () => new Map(rawMaterials.map((m) => [m.id, m])),
    [rawMaterials]
  );

  const activeWarehouses = useMemo(
    () => warehouses.filter((w) => w.is_active),
    [warehouses]
  );
  const defaultBodega = activeWarehouses[0]?.code ?? "B1";

  const itemGroups = useMemo<SearchableSelectGroup[]>(
    () => [
      {
        label: "Productos",
        options: products.map((p) => ({
          value: itemKeyOf("product", p.id),
          label: `${p.sku ?? "s/sku"} — ${p.short_name}`,
          searchTerms: `${p.sku ?? ""} ${p.short_name}`,
        })),
      },
      {
        label: "Aceites",
        options: oilTypes.map((o) => ({
          value: itemKeyOf("oil", o.id),
          label: `${o.code} — ${o.name}`,
          searchTerms: `${o.code} ${o.name}`,
        })),
      },
      {
        label: "Materia Prima",
        options: rawMaterials.map((m) => ({
          value: itemKeyOf("raw_material", m.id),
          label: `${m.code} — ${m.name}`,
          searchTerms: `${m.code} ${m.name}`,
        })),
      },
    ],
    [products, oilTypes, rawMaterials]
  );

  // Resolve an item key to its type + catalog object + code/name (for display, eval,
  // and the result report).
  const lookup = (itemKey: string) => {
    const parsed = itemKey ? parseItemKey(itemKey) : null;
    if (!parsed) return null;
    if (parsed.itemType === "product") {
      const p = productsById.get(parsed.id);
      return p
        ? { itemType: "product" as const, product: p, code: p.sku ?? "", name: p.short_name ?? "" }
        : null;
    }
    if (parsed.itemType === "oil") {
      const o = oilsById.get(parsed.id);
      return o
        ? { itemType: "oil" as const, oil: o, code: o.code, name: o.name }
        : null;
    }
    const m = rmsById.get(parsed.id);
    return m
      ? { itemType: "raw_material" as const, rawMaterial: m, code: m.code, name: m.name }
      : null;
  };

  const evalRow = (row: EditRow): BulkRowEval => {
    const info = lookup(row.itemKey);
    return evaluateBulkRow({
      itemType: info?.itemType ?? null,
      product: info && "product" in info ? info.product : null,
      oil: info && "oil" in info ? info.oil : null,
      rawMaterial: info && "rawMaterial" in info ? info.rawMaterial : null,
      location: row.location,
      signedQty: parseNum(row.quantity),
      unitCost: parseNum(row.unitCost),
      lot: row.lot.trim() || null,
      notes: null,
      reason,
      warehouses,
      oilStock,
      rmStock,
    });
  };

  const evals = useMemo(
    () => rows.map(evalRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, reason, productsById, oilsById, rmsById, warehouses, oilStock, rmStock]
  );

  const validCount = evals.filter((e) => e.ok).length;
  const errorCount = evals.filter((e) => e.complete && !e.ok).length;

  const patchRow = (key: string, patch: Partial<EditRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const pickItem = (key: string, itemKey: string) => {
    const newType = lookup(itemKey)?.itemType;
    const defaultLoc = newType === "product" ? defaultBodega : "P1";
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        // Keep a manually chosen location only when the item type is unchanged;
        // otherwise reset to the default for the new type (B1↔P1 aren't interchangeable).
        const prevType = lookup(r.itemKey)?.itemType;
        const keepLoc = r.location && prevType === newType;
        return { ...r, itemKey, location: keepLoc ? r.location : defaultLoc };
      })
    );
  };

  const removeRow = (key: string) =>
    setRows((prev) => {
      const next = prev.filter((r) => r.key !== key);
      return next.length ? next : [newRow()];
    });

  const reset = () => {
    setReasonCodeId("");
    setRows([newRow(), newRow(), newRow()]);
    setResults(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      await downloadBulkAdjustmentCatalog();
    } catch (err) {
      toast({
        title: "No se pudo generar la plantilla",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleImportFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      let parsed;
      try {
        parsed = parseBulkAdjustmentFile(e.target?.result as string);
      } catch (err) {
        toast({
          title: "No se pudo leer el archivo",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
        return;
      }
      if (parsed.fatal) {
        toast({
          title: "Formato no reconocido",
          description: parsed.fatal,
          variant: "destructive",
        });
        return;
      }
      const added: EditRow[] = [];
      const notFound: string[] = [];
      for (const r of parsed.rows) {
        const res = resolveCode(r.tipoRaw, r.codigo, products, oilTypes, rawMaterials);
        if ("error" in res) {
          notFound.push(`${r.codigo} (${res.error})`);
          continue;
        }
        added.push({
          key: String(seq.current++),
          itemKey: itemKeyOf(res.itemType, res.id),
          location: r.location || (res.itemType === "product" ? defaultBodega : "P1"),
          quantity: String(r.quantity ?? ""),
          unitCost: r.unitCost != null ? String(r.unitCost) : "",
          lot: r.lot ?? "",
        });
      }
      // Replace any still-empty rows, then append the imported ones.
      setRows((prev) => {
        const kept = prev.filter((row) => row.itemKey || row.quantity.trim());
        const next = [...kept, ...added];
        return next.length ? next : [newRow()];
      });
      toast({
        title: "Archivo importado",
        description: `${added.length} filas agregadas${
          notFound.length ? ` · ${notFound.length} no encontradas` : ""
        }`,
        variant: notFound.length ? "destructive" : "default",
      });
    };
    reader.readAsBinaryString(file);
  };

  const handleApply = async () => {
    if (!userId) {
      toast({
        title: "Error",
        description: "No se pudo identificar al usuario",
        variant: "destructive",
      });
      return;
    }
    const items: BulkAdjustmentItem[] = [];
    rows.forEach((row, i) => {
      const e = evals[i];
      if (!e.ok || !e.data) return;
      const info = lookup(row.itemKey);
      items.push({
        index: i + 1,
        tipo: e.data.item_type,
        codigo: info?.code ?? "",
        itemName: info?.name ?? "",
        data: e.data,
      });
    });
    if (items.length === 0) return;
    try {
      const res = await createBulk.mutateAsync({ items, performedBy: userId });
      setResults(res);
      const okCount = res.filter((r) => r.ok).length;
      const failCount = res.length - okCount;
      toast({
        title: "Ajuste masivo aplicado",
        description: `${okCount} aplicados${failCount > 0 ? ` · ${failCount} con error` : ""}`,
        variant: failCount > 0 ? "destructive" : "default",
      });
      onSuccess?.();
    } catch (err) {
      toast({
        title: "Error al aplicar",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  const downloadErrors = () => {
    const failed = (results ?? []).filter((r) => !r.ok);
    exportCsv(
      "errores_ajuste_masivo.csv",
      ["Fila", "Código", "Ítem", "Error"],
      failed.map((r) => [r.index, r.codigo, r.itemName, r.error ?? ""])
    );
  };

  const resultOk = (results ?? []).filter((r) => r.ok).length;
  const resultFail = (results ?? []).length - resultOk;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Ajuste Masivo de Stock
          </DialogTitle>
          <DialogDescription>
            Agrega varios ítems y cantidades en la tabla y aplícalos en un solo paso.
            Nada se ajusta hasta confirmar.
          </DialogDescription>
        </DialogHeader>

        {results ? (
          /* ---- Results view ---- */
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 rounded-md border bg-muted/40 p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="text-sm">
                  <span className="text-lg font-bold text-green-700">
                    {resultOk}
                  </span>{" "}
                  aplicados
                </span>
              </div>
              {resultFail > 0 && (
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm">
                    <span className="text-lg font-bold text-red-700">
                      {resultFail}
                    </span>{" "}
                    con error
                  </span>
                </div>
              )}
            </div>

            {resultFail > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Filas con error</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={downloadErrors}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Descargar errores
                  </Button>
                </div>
                <div className="max-h-[280px] overflow-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/60 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Fila</th>
                        <th className="px-3 py-2">Código</th>
                        <th className="px-3 py-2">Ítem</th>
                        <th className="px-3 py-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results
                        .filter((r) => !r.ok)
                        .map((r) => (
                          <tr key={r.index} className="border-t">
                            <td className="px-3 py-2 tabular-nums text-muted-foreground">
                              {r.index}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {r.codigo}
                            </td>
                            <td className="max-w-[240px] truncate px-3 py-2">
                              {r.itemName || "—"}
                            </td>
                            <td className="px-3 py-2 text-red-700">{r.error}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                onClick={() => {
                  onSuccess?.();
                  close();
                }}
              >
                Cerrar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* ---- Table builder ---- */
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex-1 space-y-1.5 sm:max-w-xs">
                <Label>Motivo (para todos los ajustes)</Label>
                <Select value={reasonCodeId} onValueChange={setReasonCodeId}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Selecciona un motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasonCodes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.code}: {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    handleImportFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9"
                  onClick={handleDownloadTemplate}
                  disabled={downloading}
                >
                  {downloading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Plantilla
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Importar archivo
                </Button>
              </div>
            </div>

            {reason && reason.adjustment_type !== "both" && (
              <p className="text-xs text-amber-700">
                Este motivo solo permite{" "}
                {reason.adjustment_type === "increment"
                  ? "incrementos (+)"
                  : "decrementos (−)"}
                . Para mezclar + y − usa un motivo de tipo “Ambos” (ej. Error de
                Conteo).
              </p>
            )}

            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2">Ítem</th>
                    <th className="w-[92px] px-2 py-2">Ubic.</th>
                    <th className="w-[84px] px-2 py-2 text-right">Cant. ±</th>
                    <th className="w-[110px] px-2 py-2 text-right">Costo</th>
                    <th className="min-w-[180px] px-2 py-2">Costo prom. / estado</th>
                    <th className="w-[40px] px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const e = evals[i];
                    const info = lookup(row.itemKey);
                    const isProduct = info?.itemType === "product";
                    const locOptions = isProduct
                      ? activeWarehouses.map((w) => w.code)
                      : PLANT_OPTIONS;
                    const signedQty = parseNum(row.quantity) ?? 0;
                    // Products need a lote on decrements, or on increments whose
                    // motivo requires one.
                    const needsLot =
                      isProduct &&
                      (signedQty < 0 ||
                        (!!reason?.requires_lot_number && signedQty > 0));
                    return (
                      <tr key={row.key} className="border-t align-top">
                        <td className="px-2 py-2">
                          <SearchableSelect
                            value={row.itemKey}
                            onValueChange={(v) => pickItem(row.key, v)}
                            groups={itemGroups}
                            placeholder="Buscar ítem…"
                            className="h-9"
                          />
                          {info && (
                            <span className="mt-1 inline-block text-[11px] text-muted-foreground">
                              {tipoTag(info.itemType)} · {info.code}
                            </span>
                          )}
                          {needsLot && (
                            <Input
                              value={row.lot}
                              onChange={(ev) =>
                                patchRow(row.key, { lot: ev.target.value })
                              }
                              placeholder="Lote (requerido)"
                              className="mt-1 h-8"
                            />
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <Select
                            value={row.location}
                            onValueChange={(v) =>
                              patchRow(row.key, { location: v })
                            }
                            disabled={!info}
                          >
                            <SelectTrigger className="h-9 px-2">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {locOptions.map((code) => (
                                <SelectItem key={code} value={code}>
                                  {code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={row.quantity}
                            onChange={(ev) =>
                              patchRow(row.key, { quantity: ev.target.value })
                            }
                            inputMode="decimal"
                            placeholder="+/−"
                            className="h-9 text-right"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={row.unitCost}
                            onChange={(ev) =>
                              patchRow(row.key, { unitCost: ev.target.value })
                            }
                            inputMode="decimal"
                            placeholder="opcional"
                            disabled={signedQty < 0}
                            className="h-9 text-right"
                          />
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {!info ? (
                            <span className="text-muted-foreground">—</span>
                          ) : e.complete && !e.ok ? (
                            <span className="text-red-700">{e.error}</span>
                          ) : e.ok && e.hasPreview ? (
                            e.costChanged ? (
                              <span>
                                <span className="text-muted-foreground">
                                  ${formatCurrency(e.avgBefore)}
                                </span>{" "}
                                →{" "}
                                <span className="font-semibold text-amber-700">
                                  ${formatCurrency(e.avgAfter)}
                                </span>
                              </span>
                            ) : (
                              <span className="text-green-700">
                                ${formatCurrency(e.avgBefore)} · sin cambio
                              </span>
                            )
                          ) : (
                            <span className="text-muted-foreground">
                              Ingresa cantidad
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-red-600"
                            onClick={() => removeRow(row.key)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => setRows((prev) => [...prev, newRow()])}
              >
                <Plus className="mr-2 h-4 w-4" />
                Agregar ítem
              </Button>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-green-700">{validCount}</span>{" "}
                listas
                {errorCount > 0 && (
                  <>
                    {" "}
                    · <span className="font-medium text-red-700">{errorCount}</span>{" "}
                    con problema
                  </>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancelar
              </Button>
              <Button
                onClick={handleApply}
                disabled={validCount === 0 || createBulk.isPending || !userId}
              >
                {createBulk.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Aplicar {validCount > 0 ? `(${validCount})` : ""}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
